/**
 * useSecurityTree — React hook for security tree state management.
 *
 * Wraps the pure tree-logic functions with React state and provides
 * a stable API for the UI component to consume.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { PlantAccessEntry, SecurityTreeNodeDto, TreeFilter, TreeNode } from './types';
import {
  buildTree,
  computeFilteredParentStates,
  getChangedAccess,
  getChildren,
  getRootNodes,
  getVisibleNodeIds,
  hasChanges,
  toggleNode,
} from './tree-logic';

export interface UseSecurityTreeReturn {
  /** The tree to render (parent states recomputed for filtered view). */
  tree: Map<string, TreeNode>;
  /** Root-level nodes. */
  roots: TreeNode[];
  /** Get children of a node. */
  getNodeChildren: (nodeId: string) => TreeNode[];
  /** Toggle a node's access state (click handler). */
  toggle: (nodeId: string) => void;
  /** Apply a geographic filter. */
  setFilter: (filter: TreeFilter) => void;
  /** Current filter. */
  filter: TreeFilter;
  /** Set of visible node IDs (based on filter). */
  visibleNodeIds: Set<string>;
  /** Whether any changes have been made since load. */
  isDirty: boolean;
  /** Extract the save payload (only changed plants). */
  getSavePayload: () => PlantAccessEntry[];
  /** Reset tree to initial state. */
  reset: () => void;
}

export function useSecurityTree(data: SecurityTreeNodeDto[] | undefined): UseSecurityTreeReturn {
  const [tree, setTree] = useState<Map<string, TreeNode>>(() => new Map());
  const [filter, setFilter] = useState<TreeFilter>({});
  const initialTreeRef = useRef<Map<string, TreeNode>>(new Map());

  // Build tree when data changes
  useMemo(() => {
    if (!data || data.length === 0) return;
    const built = buildTree(data);
    setTree(built);
    initialTreeRef.current = buildTree(data); // separate clone for dirty comparison
  }, [data]);

  const visibleNodeIds = useMemo(
    () => getVisibleNodeIds(tree, filter),
    [tree, filter]
  );

  const isFiltered = Boolean(filter.areaId || filter.countryId || filter.regionId);

  // When filtered, recompute parent states based on visible children only.
  // This is the display tree — what gets rendered.
  const displayTree = useMemo(
    () => isFiltered ? computeFilteredParentStates(tree, visibleNodeIds) : tree,
    [tree, visibleNodeIds, isFiltered]
  );

  const roots = useMemo(() => getRootNodes(displayTree), [displayTree]);

  const getNodeChildren = useCallback(
    (nodeId: string) => getChildren(displayTree, nodeId),
    [displayTree]
  );

  const toggle = useCallback((nodeId: string) => {
    setTree(prev => toggleNode(prev, nodeId));
  }, []);

  const isDirty = useMemo(
    () => hasChanges(tree, initialTreeRef.current),
    [tree]
  );

  const getSavePayload = useCallback(
    () => getChangedAccess(tree),
    [tree]
  );

  const reset = useCallback(() => {
    if (initialTreeRef.current.size > 0) {
      setTree(new Map(
        [...initialTreeRef.current].map(([id, node]) => [
          id,
          { ...node, childIds: [...node.childIds], siblingIds: [...node.siblingIds] },
        ])
      ));
    }
  }, []);

  return {
    tree: displayTree,
    roots,
    getNodeChildren,
    toggle,
    setFilter,
    filter,
    visibleNodeIds,
    isDirty,
    getSavePayload,
    reset,
  };
}
